import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Elements,
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  loadStripe,
  type PaymentRequest as StripePaymentRequest,
  type PaymentRequestPaymentMethodEvent,
  type PaymentRequestShippingAddressEvent,
  type PaymentRequestShippingOptionEvent,
} from "@stripe/stripe-js";

import { Product } from "../../api-client";
import { AddToCart, AddressData, GuestAddress, ParcelRequest, ParcelResponse, ShippingRate } from "../../api-client/models";
import { useAuthStore } from "../../store/AuthStore";
import { addToCart, getCartItems, type GuestCartProductData } from "../../services/cart";
import { calculateStripeTotal, parcelsCreate, shippingCreate } from "../../services/payment";
import { shippingAPi } from "../../api";
import { useStripePaymentIntent } from "../../hooks/ShoppingPaymentHooks/useStripePaymentIntent";
import {
  buildPaymentItems,
  buildPaymentTotal,
  isIllinois,
  mapRateToShippingOption,
  parseShippingAmount,
  toMinorUnits,
} from "../../utils/stripeHelpers";
import { PAYMENT_DEFAULTS, STRIPE_CONFIG } from "../../config/env";

// ─── Config ─────────────────────────────────────────────────────────────────

const stripePromise = STRIPE_CONFIG.PUBLIC_KEY
  ? loadStripe(STRIPE_CONFIG.PUBLIC_KEY)
  : null;
const stripeMerchantCountry = STRIPE_CONFIG.MERCHANT_COUNTRY;

const DEFAULT_PHONE = PAYMENT_DEFAULTS.GUEST_PHONE;
const DEFAULT_EMAIL = PAYMENT_DEFAULTS.GUEST_EMAIL;

// ─── Inner component (needs to live inside <Elements>) ───────────────────────

interface ProductApplePayInnerProps {
  product: Product;
  quantity: number;
  selectedSizeId: number | null;
  selectedColorId: number | null;
  stock: number;
  onSuccess?: () => void;
}

function ProductApplePayInner({
  product,
  quantity,
  selectedSizeId,
  selectedColorId,
  stock,
  onSuccess,
}: ProductApplePayInnerProps) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<StripePaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Resolve current product variant from color+size selection
  const productItem =
    product.product_items.find(
      (item) =>
        item.distribution?.color?.id === selectedColorId &&
        item.distribution?.size?.id === selectedSizeId,
    ) ?? null;

  const subtotal = product.price * quantity;

  // Refs for use inside async event handlers (no stale closures)
  const productItemRef = useRef(productItem);
  const subtotalRef = useRef(subtotal);
  const quantityRef = useRef(quantity);
  const taxRateRef = useRef(0);
  const isGuestRef = useRef(!useAuthStore.getState().accessToken);
  const shippingStateRef = useRef("");
  const parcelRef = useRef<ParcelResponse[] | null>(null);
  const ratesByIdRef = useRef<Record<string, ShippingRate>>({});
  const selectedRateRef = useRef<ShippingRate | null>(null);

  // Synchronous ref updates so event handlers always see current values
  productItemRef.current = productItem;
  subtotalRef.current = subtotal;
  quantityRef.current = quantity;
  isGuestRef.current = !useAuthStore.getState().accessToken;

  // Invalidate parcel cache when quantity changes
  useEffect(() => {
    parcelRef.current = null;
  }, [quantity]);

  // Keep payment sheet totals in sync when subtotal changes while sheet is open
  useEffect(() => {
    if (!paymentRequest) return;
    try {
      paymentRequest.update({
        total: buildPaymentTotal(subtotal, 0, 0, 0),
        displayItems: buildPaymentItems(subtotal, 0, 0, 0),
      });
    } catch {
      // update() throws if the sheet is already open — safe to ignore
    }
  }, [subtotal, paymentRequest]);

  const { createGuestStripePaymentIntent, createStripePaymentIntent } =
    useStripePaymentIntent();

  // One-time setup: create payment request and register all event handlers
  useEffect(() => {
    if (!stripe) return;

    const pr = stripe.paymentRequest({
      country: stripeMerchantCountry,
      currency: "usd",
      total: buildPaymentTotal(subtotalRef.current, 0, 0, 0),
      displayItems: buildPaymentItems(subtotalRef.current, 0, 0, 0),
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
      requestShipping: true,
      shippingOptions: [
        {
          id: "pending",
          label: "Calculating shipping…",
          detail: "Rates will be calculated based on your address",
          amount: 0,
        },
      ],
    });

    pr.canMakePayment().then((result) => {
      if (result?.applePay) {
        setPaymentRequest(pr);
        setCanMakePayment(true);
      }
    });

    // ── shippingaddresschange ─────────────────────────────────────────────
    pr.on(
      "shippingaddresschange",
      async (event: PaymentRequestShippingAddressEvent) => {
        const { shippingAddress } = event;

        try {
          let parcels: ParcelResponse[];
          if (parcelRef.current) {
            parcels = parcelRef.current;
          } else {
            const parcelData: ParcelRequest = {
              products: quantityRef.current,
              include_insurance: false,
            };
            const parcelResult = await parcelsCreate(parcelData);
            const raw = parcelResult.data as ParcelResponse | ParcelResponse[];
            parcels = Array.isArray(raw) ? raw : [raw];
            parcelRef.current = parcels;
          }

          const city = shippingAddress.city || "";
          const state = shippingAddress.region || "";
          const zip = shippingAddress.postalCode || "";

          if (!city || !state || !zip) {
            event.updateWith({ status: "invalid_shipping_address" });
            return;
          }

          shippingStateRef.current = state;

          const partialAddress: AddressData = {
            name: "Guest",
            company: "",
            street1: shippingAddress.addressLine?.[0] || "1 Main St",
            street3: "",
            street_no: "",
            city,
            state,
            zip,
            phone: DEFAULT_PHONE,
            email: DEFAULT_EMAIL,
            is_residential: true,
            metadata: "",
          };

          const shippingResult = await shippingCreate({
            address: partialAddress,
            parcels,
            include_insurance: true,
          });

          const rawShippings = shippingResult.data?.shippings_data ?? [];
          const rates: ShippingRate[] = rawShippings.flatMap(
            (d: { rates: ShippingRate[] }) => d.rates ?? [],
          );

          if (rates.length === 0) {
            event.updateWith({ status: "invalid_shipping_address" });
            return;
          }

          ratesByIdRef.current = Object.fromEntries(
            rates.map((r) => [r.object_id, r]),
          );

          const sorted = [...rates].sort(
            (a, b) =>
              parseShippingAmount(a.amount) - parseShippingAmount(b.amount),
          );
          selectedRateRef.current = sorted[0];
          const firstShipping = parseShippingAmount(sorted[0].amount);

          const currentItem = productItemRef.current;
          const calcData = await calculateStripeTotal({
            items: currentItem?.id
              ? [{ product_item_id: currentItem.id, quantity: quantityRef.current }]
              : [],
            address: { state },
            shipping_cost: firstShipping,
            discount_amount: 0,
            currency: "usd",
          });

          const taxAmount = parseFloat(calcData.tax_amount) || 0;
          const calcSubtotal = parseFloat(calcData.subtotal) || subtotalRef.current;
          const discount = parseFloat(calcData.discount) || 0;
          const totalCents =
            calcData.amount_cents ||
            toMinorUnits(calcSubtotal + taxAmount + firstShipping - discount);

          event.updateWith({
            status: "success",
            shippingOptions: sorted.map(mapRateToShippingOption),
            total: { label: "Goal Wear", amount: totalCents },
            displayItems: buildPaymentItems(calcSubtotal, taxAmount, firstShipping, discount),
          });
        } catch (error) {
          console.error("[ProductApplePay] shippingaddresschange failed:", error);
          event.updateWith({ status: "fail" });
        }
      },
    );

    // ── shippingoptionchange ──────────────────────────────────────────────
    pr.on(
      "shippingoptionchange",
      async (event: PaymentRequestShippingOptionEvent) => {
        const { shippingOption } = event;
        const shippingAmountDollars = shippingOption.amount / 100;

        const matchedRate = ratesByIdRef.current[shippingOption.id];
        if (matchedRate) selectedRateRef.current = matchedRate;

        try {
          const currentItem = productItemRef.current;
          const calcData = await calculateStripeTotal({
            items: currentItem?.id
              ? [{ product_item_id: currentItem.id, quantity: quantityRef.current }]
              : [],
            address: { state: shippingStateRef.current },
            shipping_cost: shippingAmountDollars,
            discount_amount: 0,
            currency: "usd",
          });

          const taxAmount = parseFloat(calcData.tax_amount) || 0;
          const calcSubtotal = parseFloat(calcData.subtotal) || subtotalRef.current;
          const discount = parseFloat(calcData.discount) || 0;
          const totalCents =
            calcData.amount_cents ||
            toMinorUnits(calcSubtotal + taxAmount + shippingAmountDollars - discount);

          event.updateWith({
            status: "success",
            total: { label: "Goal Wear", amount: totalCents },
            displayItems: buildPaymentItems(calcSubtotal, taxAmount, shippingAmountDollars, discount),
          });
        } catch {
          const taxAmount = isIllinois(shippingStateRef.current)
            ? subtotalRef.current * taxRateRef.current
            : 0;
          event.updateWith({
            status: "success",
            total: buildPaymentTotal(subtotalRef.current, taxAmount, shippingAmountDollars, 0),
            displayItems: buildPaymentItems(subtotalRef.current, taxAmount, shippingAmountDollars, 0),
          });
        }
      },
    );

    // ── paymentmethod ─────────────────────────────────────────────────────
    pr.on(
      "paymentmethod",
      async (event: PaymentRequestPaymentMethodEvent) => {
        const { shippingAddress, paymentMethod, payerEmail, payerPhone, payerName } =
          event;

        const currentItem = productItemRef.current;

        if (!currentItem?.id) {
          event.complete("fail");
          toast.error("Please select a size and color.");
          return;
        }
        if (!shippingAddress) {
          event.complete("fail");
          toast.error("Shipping address not received from Apple Pay.");
          return;
        }
        if (!selectedRateRef.current) {
          event.complete("fail");
          toast.error("No shipping rate selected. Please try again.");
          return;
        }
        if (!payerEmail) {
          event.complete("fail");
          toast.error("Apple Pay did not provide an email address.");
          return;
        }

        setIsProcessing(true);

        try {
          const recipientName =
            shippingAddress.recipient || payerName || "Guest Customer";
          const shippingCost = parseShippingAmount(selectedRateRef.current.amount);
          const country = (shippingAddress.country || "US").toUpperCase();
          const phone = shippingAddress.phone || payerPhone || DEFAULT_PHONE;

          const street1 = shippingAddress.addressLine?.[0] || "";
          const street3 = shippingAddress.addressLine?.slice(1).join(", ") || "";
          const city = shippingAddress.city || "";
          const state = shippingAddress.region || "";
          const zip = shippingAddress.postalCode || "";

          const addressDataPayload: AddressData = {
            name: recipientName,
            company: "",
            street1,
            street3,
            street_no: "",
            city,
            state,
            zip,
            phone,
            email: payerEmail,
            is_residential: true,
            metadata: "",
          };

          const guestAddressPayload: GuestAddress = {
            name: recipientName,
            street1,
            street3,
            city,
            state,
            zip,
            country,
            phone,
            email: payerEmail,
            is_residential: true,
          };

          let clientSecret: string;

          if (isGuestRef.current) {
            // Guest: use items array directly — no cart needed
            const intentData = await createGuestStripePaymentIntent({
              guest_email: payerEmail,
              guest_first_name: recipientName.split(" ")[0] || "",
              guest_last_name: recipientName.split(" ").slice(1).join(" ") || "",
              items: [{ product_item_id: currentItem.id, quantity: quantityRef.current }],
              address: guestAddressPayload,
              shipping_cost: shippingCost,
              discount_amount: 0,
              rates: [selectedRateRef.current],
              currency: "usd",
            });

            const secret = intentData.client_secret ?? intentData.clientSecret;
            if (!secret) throw new Error("Backend did not return a client_secret.");
            clientSecret = secret;
          } else {
            // Auth: add to cart + save address in parallel, then get cart ID
            const cartOperation: AddToCart = {
              product_item_id: currentItem.id,
              quantity: quantityRef.current,
            };
            const guestData: GuestCartProductData = {
              product_name: product.name,
              price: product.price,
              color: currentItem.distribution?.color?.name || "",
              size: currentItem.distribution?.size?.name || "",
              image: currentItem.images?.[0]?.image || (product as any).image || "",
            };

            const [, addrResponse, cartData] = await Promise.all([
              addToCart(cartOperation, guestData),
              shippingAPi.shippingSaveAddressCreate({ addressData: addressDataPayload }),
              getCartItems(),
            ]);

            const cartId = (cartData as any)?.id ?? null;
            const addressId = (addrResponse.data as { id?: number }).id ?? null;

            if (!cartId) throw new Error("Cart not found. Please try again.");
            if (!addressId) throw new Error("Could not save shipping address. Please try again.");

            const intentData = await createStripePaymentIntent({
              shopping_car: cartId,
              address_id: addressId,
              shipping_cost: shippingCost,
              discount_amount: 0,
              rates: [selectedRateRef.current],
              currency: "usd",
            });

            const secret = intentData.client_secret ?? intentData.clientSecret;
            if (!secret) throw new Error("Backend did not return a client_secret.");
            clientSecret = secret;
          }

          const { error: confirmError, paymentIntent } =
            await stripe.confirmCardPayment(
              clientSecret,
              { payment_method: paymentMethod.id },
              { handleActions: false },
            );

          if (confirmError) {
            event.complete("fail");
            toast.error(confirmError.message || "Payment failed.");
            return;
          }

          event.complete("success");

          if (paymentIntent?.status === "requires_action") {
            const { error: actionError } =
              await stripe.confirmCardPayment(clientSecret);
            if (actionError) {
              toast.error(actionError.message || "3DS authentication failed.");
              return;
            }
          }

          toast.success("Payment completed successfully!");
          onSuccess?.();
        } catch (error) {
          event.complete("fail");
          toast.error(
            error instanceof Error
              ? error.message
              : "Payment could not be completed.",
          );
        } finally {
          setIsProcessing(false);
        }
      },
    );

    return () => {
      pr.off("shippingaddresschange");
      pr.off("shippingoptionchange");
      pr.off("paymentmethod");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe]);

  // Only render when Apple Pay is available and a valid variant is selected
  if (!canMakePayment || !paymentRequest || !productItem || stock <= 0) {
    return null;
  }

  return (
    <div className="w-32">
      {isProcessing && (
        <p className="mb-1 text-center text-xs text-gray-400">Processing…</p>
      )}
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: "default",
              theme: "dark",
              height: "40px",
            },
          },
        }}
      />
    </div>
  );
}

// ─── Public component (wraps inner in its own <Elements> provider) ────────────

interface ProductApplePayProps {
  product: Product;
  quantity: number;
  selectedSizeId: number | null;
  selectedColorId: number | null;
  stock: number;
  onSuccess?: () => void;
}

export const ProductApplePay: React.FC<ProductApplePayProps> = (props) => {
  if (!stripePromise) return null;
  return (
    <Elements stripe={stripePromise}>
      <ProductApplePayInner {...props} />
    </Elements>
  );
};

export default ProductApplePay;
